<template>
  <section
    v-if="codProducao && config"
    class="wrapper"
  >
    <div v-if="pollError" class="poll-error-indicator" title="Erro de conexão">
      <v-icon x-small color="red">mdi-circle</v-icon>
    </div>
    <!-- Playout WebRTC. The player stays mounted (a warm standby connection) the whole
         time the playlist is active, but is only made VISIBLE once a graphic is on air.
         Before the first Take it is connected yet hidden, so /cena stays black
         (a pre-take preview renders on the same engine but never reaches air); at Take,
         revealing the already-connected stream is instant — no cold-connect delay. -->
    <div
      v-if="isPlayoutActive"
      style="width: 100%"
      class="cenaRow"
      :style="{ visibility: isPlayoutOnAir ? 'visible' : 'hidden' }"
    >
      <WebRtcPlayer
        :signaling-url="playoutSignalingUrl"
        :controls="false"
        :id="'playout-' + codProducao"
        style="width: 100%; height: 100vh; object-fit: contain;"
      />
    </div>
  </section>
</template>

<script>
import { apiClient } from "@/services/api";
import WebRtcPlayer from "@/components/WebRtcPlayer.vue";

export default {
  name: "CenaView",
  components: { WebRtcPlayer },
  data() {
    return {
      codProducao: null,
      config: null,
      configInterval: null,
      onAirInterval: null,
      requestInterceptorId: null,
      fetchingConfig: false,
      fetchingOnAir: false,
      // Drives the reveal of the warm playout stream. Polled on its own fast, tiny
      // loop (not the heavier config) so a Take shows on /cena near-instantly.
      playoutOnAir: false,
      configFailures: 0,
    };
  },
  async created() {
    // Broadcast display must never scroll: a full-frame 100vh element + browser
    // chrome can overflow and show a scrollbar, which also steals ~2px of width
    // and letterboxes the WebRTC video. Same guard as TarjaView.
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    // Get production code from query or localStorage
    const raw =
      this.$route.query.cod_producao ||
      localStorage.getItem("@socialcam/selected_option_production");
    this.codProducao = raw ? Number(raw) : null;

    if (!this.codProducao) return;

    // Set token auth if provided via query param (same pattern as TarjaView)
    const token = this.$route.query.token;
    if (token) {
      this.requestInterceptorId = apiClient.interceptors.request.use((config) => {
        config.headers["x-access-token"] = token;
        return config;
      });
    }

    // Initial fetch
    await this.fetchConfig();

    // Poll for updates. Config (which carries the signaling URL — it only changes on
    // activate/deactivate) is slow. The on-air flag gets its OWN fast, tiny loop: the
    // reveal is just a visibility flip on the already-connected player, so this poll is
    // the whole reveal latency — kept at 200ms so a Take shows on /cena imperceptibly
    // close to the operator monitor.
    this.configInterval = setInterval(this.fetchConfig, 3000);
    this.onAirInterval = setInterval(this.fetchOnAir, 200);
  },
  methods: {
    async fetchConfig() {
      if (this.fetchingConfig || this.configFailures >= 10) return;
      this.fetchingConfig = true;
      try {
        const { data } = await apiClient.get(
          `globographics/config/${this.codProducao}`
        );
        this.config = data;
        this.configFailures = 0;
      } catch (error) {
        this.configFailures++;
        // Keep the section rendered on a failed first fetch so the page stays black
        // rather than collapsing to the router's blank default.
        if (!this.config) {
          this.config = {};
        }
        if (this.configFailures >= 10) {
          clearInterval(this.configInterval);
        }
      } finally {
        this.fetchingConfig = false;
      }
    },
    // Fast, tiny poll of just the on-air flag (single-query endpoint) — only while the
    // playout stream is the active surface. Reveals the warm WebRTC stream the instant
    // a Take lands. Skips its own request while one is in flight.
    async fetchOnAir() {
      if (this.fetchingOnAir || !this.isPlayoutActive) return;
      this.fetchingOnAir = true;
      try {
        const { data } = await apiClient.get(`playout/on-air/${this.codProducao}`);
        this.playoutOnAir = !!data?.on_air;
      } catch (error) {
        // Best-effort — a missed tick just delays the reveal by one interval.
      } finally {
        this.fetchingOnAir = false;
      }
    },
  },
  computed: {
    // Connection-lost indicator for an unattended broadcast display. Keyed on the
    // config poll, which is the surviving heartbeat now that the legacy
    // cena-em-exibicao poll (whose failures used to drive this) is gone.
    pollError() {
      return this.configFailures >= 5;
    },
    isPlayoutActive() {
      return !!this.config?.dsc_playout_signaling_url;
    },
    // Whether a graphic is genuinely on air — from the fast on-air poll. The warm
    // playout stream is revealed only when true; otherwise /cena stays black
    // (a pre-take preview renders on the engine but must not reach air).
    isPlayoutOnAir() {
      return this.playoutOnAir;
    },
    playoutSignalingUrl() {
      // Engine-native WebRTC signaling endpoint, built per-engine by the backend
      // (ws://<engine-host>:<engine-webrtcPort>/ws) and exposed via GloboGraphicsConfig.
      return this.config?.dsc_playout_signaling_url || null;
    },
  },
  beforeDestroy() {
    clearInterval(this.configInterval);
    clearInterval(this.onAirInterval);
    if (this.requestInterceptorId !== null) {
      apiClient.interceptors.request.eject(this.requestInterceptorId);
    }
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
.wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  position: relative;
  /* Broadcast surface: black is the truthful "nothing on air" state. The WebRTC
     player covers this whenever a graphic is live; it shows through before the
     first take, while the player is connected-but-hidden, and in the letterbox
     bars when the window isn't 16:9. */
  background-color: #000;
}
.cenaRow {
  display: flex;
  justify-content: center;
  align-items: center;
}
.poll-error-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 10;
}
</style>
